import { setDataFromTreeToCache } from 'lib/apolloClient';
import { GetStaticProps, NextPage } from 'next';
import {
  getAllPostsSlugs,
  getPostData,
  findRedirectPath,
  PageParams,
} from '../lib/posts';
import { SeoHead, PageWrapper } from 'layouts';
import { Post } from 'components';
import {
  PostAdditionalFieldsFragmentFragment,
  PostFragmentFragment,
  VideoData,
} from 'types';
import { PreviewModeBanner } from '../components/PreviewBanner';
import { PreviewDataProps } from '../lib/previewApi';
import { getYoutubeSeoMetadata } from 'utils/youTubeSeoMetadata';
import { logger } from 'utils';
import { EventIds } from 'lib';

const log = logger.getInstance('Post');

type PostProps = {
  postData:
    | (PostFragmentFragment &
        PostAdditionalFieldsFragmentFragment & { id: string })
    | null;
  videoData?: VideoData;
  isPreviewEnabled?: boolean;
};

export type LessonPostsNodes = NonNullable<
  NonNullable<
    NonNullable<NonNullable<PostFragmentFragment['lessonsPosts']>['nodes']>[0]
  >['posts']
>['nodes'];

export const PostPage: NextPage<PostProps> = ({
  postData,
  videoData,
  isPreviewEnabled,
}) => {
  const firstLessonCategoryPosts =
    postData?.lessonsPosts?.nodes?.[0]?.posts?.nodes || [];

  return (
    <PageWrapper
      translations={postData?.translations}
      headerColor={postData?.postAdditionalFields?.accentColor}
    >
      <SeoHead
        seoHead={postData?.seo?.fullHead}
        videoSeoMetaTags={videoData?.videoSeoMetaTags}
      />
      {isPreviewEnabled && <PreviewModeBanner />}
      <Post postData={postData} relatedPosts={firstLessonCategoryPosts} />
    </PageWrapper>
  );
};

export async function getStaticPaths() {
  let paths: string[] = [];
  // Variable defined by Vercel project, only on production build (previews won't build everything)
  if (process.env.BUILD_ALL_PAGES === 'true') {
    paths = await getAllPostsSlugs();
  }

  return { paths, fallback: 'blocking' };
}

export const getStaticProps: GetStaticProps<{}, PageParams, PreviewDataProps> =
  async (props) => {
    const slug = props?.params?.post;
    // If slug is part of redirect list, we redirect it.
    // This can happen when we navigate through next/link on the client side, otherwise it's handled by Next
    if (slug) {
      const redirect = findRedirectPath(slug, 'en');
      if (redirect) {
        return {
          redirect,
        };
      }
    }

    // Fetch data outside `useQuery` otherwise apolloClient will cache the result and return accumulated post data in cache, which is transferred to the client
    // After 200 generated pages, this was generating page with 20MB of data

    let postData = null;
    try {
      postData = await getPostData(props);
    } catch (e) {
      if (props.preview) {
        return {
          redirect: {
            // NOTE: "api/exit-preview" will redirect to [post-slug]/api/exit-preview
            destination: '/api/exit-preview',
            permanent: false,
          },
        };
      } else {
        throw e;
      }
    }

    if (postData === null) {
      return {
        notFound: true,
      };
    }

    const dataFromTreeToCacheFn = setDataFromTreeToCache(PostPage, {
      componentProps: { postData },
      onDemandRevalidation:
        process.env.ON_DEMAND_REVALIDATION_FOR_POSTS === 'true',
    });

    const propsResult = await dataFromTreeToCacheFn(props);
    propsResult.props = {
      ...propsResult.props,
      isPreviewEnabled: !!props.preview,
      postData,
    };

    // Fetch Youtube data for video posts with silently failed
    // we don't want to break the page if we can't get the data
    // so we failed silently and just lot the error
    try {
      const youTubeApiKey = process.env.NEXT_PUBLIC_YOUTUBE_API_KEY;
      if (propsResult.props.videoData && youTubeApiKey) {
        const videoData = propsResult.props.videoData;
        videoData.videoSeoMetaTags = await getYoutubeSeoMetadata(
          videoData.videoIds,
          youTubeApiKey
        );
      }
    } catch (e) {
      log.error(
        'Failed to get YouTube metadata',
        EventIds.PostPageYouTubeSeoError,
        e
      );
    }

    return propsResult;
  };

export default PostPage;
